import { useState, useEffect } from "react";
import "./Offices.scss";
import { createOffice, updateOffice } from "../../api/offices.api";
import OfficeScheduleEditor from "./OfficeScheduleEditor";

const OfficeForm = ({ officeData, closeForm, refreshData }) => {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    schedule: "",
    phone: "",
    image: null,
  });

  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (officeData) {
      setFormData({
        name: officeData.name || "",
        address: officeData.address || "",
        schedule: officeData.schedule || "",
        phone: officeData.phone || "",
        image: null,
      });
      setPreview(officeData.image);
    }
  }, [officeData]);

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const dataToSend = new FormData();
    dataToSend.append("name", formData.name);
    dataToSend.append("address", formData.address);
    dataToSend.append("schedule", formData.schedule);
    dataToSend.append("phone", formData.phone);

    if (formData.image instanceof File) {
      dataToSend.append("image", formData.image);
    }

    try {
      if (officeData?._id) {
        await updateOffice(officeData._id, dataToSend);
      } else {
        await createOffice(dataToSend);
      }
      refreshData();
      closeForm();
    } catch (error) {
      if (error.response) {
        console.error("Mensaje del servidor:", error.response.data.msg);
        console.error("Detalle del error:", error.response.data.error);
      } else if (error.request) {
        console.error("El servidor no respondió:", error.request);
      } else {
        console.error("Error de configuración de Axios:", error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="OfficeFormContainer">
      <form onSubmit={handleSubmit} className="OfficeForm">
        <div className="OfficeForm__grid">
          <div className="OfficeForm__main">
            <div className="form-group">
              <label htmlFor="officeName">Nombre de la Oficina</label>
              <input
                id="officeName"
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                placeholder="Ej: Oficina Central, Sucursal Norte..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="officeAddress">Dirección</label>
              <input
                id="officeAddress"
                type="text"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                required
                placeholder="Calle, número, ciudad, código postal..."
              />
              <small className="form-hint">
                Esta dirección se usará para redirigir al cliente a Google Maps.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="officeSchedule">Horario</label>
              <OfficeScheduleEditor
                value={formData.schedule}
                onChange={(content) =>
                  setFormData({ ...formData, schedule: content })
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="officePhone">Teléfono</label>
              <input
                id="officePhone"
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                required
                placeholder="Ej: +34 600 123 456"
              />
            </div>
          </div>

          <aside className="OfficeForm__sidebar">
            <div className="form-group">
              <label htmlFor="officeImage">Imagen de la Oficina</label>
              <input
                id="officeImage"
                type="file"
                accept="image/*"
                required={!officeData}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setFormData((prev) => ({ ...prev, image: file }));
                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />
              {preview && (
                <div className="image-preview">
                  <img src={preview} alt="Vista previa" />
                </div>
              )}
            </div>
          </aside>
        </div>

        <div className="form-actions">
          <button type="button" onClick={closeForm} className="btn-cancel">
            Cancelar
          </button>
          <button type="submit" disabled={submitting} className="btn-save">
            {submitting
              ? "Guardando..."
              : officeData
                ? "Actualizar Oficina"
                : "Crear Oficina"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OfficeForm;
